import Box from 'components/shared/Box'
import Text from 'components/shared/Text'

import Price from 'components/shared/Price'
import Button from 'components/shared/Button'
import { RoomTileWrapper, RoomTileGallery, CtaWrapper } from './styles'

import { FormattedMessage, useIntl } from 'react-intl'
import { useResolutions } from 'store/resolutions'

import { Room } from 'store/api-slice/types'
import InlineTextList from 'components/shared/InlineTextList'
import { useAppSelector } from 'store/hooks'
import { useAccommodation } from 'store/accommodation-slice/hooks'
import { removeRoom, updateChosenRooms } from 'store/accommodation-slice'
import { useAppDispatch } from 'store/hooks'
import { scrollTo } from 'helpers/animateScroll'
import RoomTileBadges from './components/RoomTileBadges'
import { useMemo } from 'react'
import { GallerySlider } from 'components/shared/GallerySlider'

interface RoomTileProps {
    room: Room
    adults: number
    kids: number
    bungalowIndex: number
    roomIndex: number
}

const RoomTile = ({
    room,
    adults,
    kids,
    bungalowIndex,
    roomIndex,
}: RoomTileProps) => {
    const { formatMessage } = useIntl()
    const { isTablet } = useResolutions()

    const { dates, bungalows } = useAppSelector((state) => state.accommodation)

    const cartAccommodation = useAppSelector(
        (state) => state.cart.items.accommodation
    )

    const dispatch = useAppDispatch()
    const {
        doubleBedCount,
        pictures,
        singleBedCount,
        sofaBedCount,
        rateName,
        totalPrice,
        occupancyId,
        roomId,
        productId,
        rateId,
        badges,
    } = room
    const { removeAccommodationFromCart, isAccommodationLoading } =
        useAccommodation(productId)

    const isThisRoomInRedux = useMemo(() => {
        const correspondingBungalowInRedux = bungalows.find(
            ({ room }) => room?.bungalowIndex === bungalowIndex - 1
        )
        const { room: roomInRedux } = correspondingBungalowInRedux || {}

        return (
            roomInRedux &&
            roomInRedux.roomId === roomId &&
            roomInRedux.occupancyId === occupancyId &&
            roomInRedux.rateId === rateId
        )
    }, [bungalowIndex, bungalows, roomId, occupancyId, rateId])

    const removeRoomFromRedux = () => {
        dispatch(removeRoom(bungalowIndex - 1))
    }

    const updateRoomInRedux = () => {
        if (!roomId || !occupancyId || !rateId || !productId || !rateName)
            return

        dispatch(
            updateChosenRooms({
                bungalowIndex: bungalowIndex - 1,
                occupancyId,
                roomId,
                rateId,
                productId,
                rateName,
            })
        )
    }

    const handleChooseRoom = async () => {
        if (cartAccommodation) {
            await removeAccommodationFromCart({ editPoint: 'offers' })
        }

        updateRoomInRedux()

        if (bungalows.length > bungalowIndex) {
            scrollTo({
                id: `accommodation-bungalows-offer-${bungalowIndex + 1}`,
                duration: 500,
            })
        } else {
            scrollTo({
                position: document.body.scrollHeight,
                duration: 500,
            })
        }
    }

    const handleRemoveRoom = async () => {
        if (cartAccommodation) {
            await removeAccommodationFromCart()
            return
        }
        removeRoomFromRedux()
    }

    const getBedsInfo = () => {
        const items = []

        singleBedCount &&
            +singleBedCount > 0 &&
            items.push(
                `${singleBedCount}x ${formatMessage({
                    id: 'accommodation.bungalow_offer.room_tile_single_bed',
                    defaultMessage: 'łóżko pojedyncze',
                })}`
            )

        doubleBedCount &&
            +doubleBedCount > 0 &&
            items.push(
                `${doubleBedCount}x ${formatMessage({
                    id: 'accommodation.bungalow_offer.room_tile_double_bed',
                    defaultMessage: 'łóżko podwójne',
                })}`
            )

        sofaBedCount &&
            +sofaBedCount > 0 &&
            items.push(
                `${sofaBedCount}x ${formatMessage({
                    id: 'accommodation.bungalow_offer.room_tile_sofa',
                    defaultMessage: 'rozkładana sofa',
                })}`
            )

        return items
    }

    const guests =
        adults + kids > 1
            ? formatMessage(
                  {
                      id: 'accommodation.bungalow_offer.guests',
                      defaultMessage: '{guests} gości',
                  },
                  { guests: adults + kids }
              )
            : formatMessage({
                  id: 'accommodation.bungalow_offer.guest',
                  defaultMessage: '1 gość',
              })

    const days = dates.length
    let stayLength = formatMessage(
        {
            id: 'accommodation.bungalow_offer.one_night',
            defaultMessage: 'jedna noc',
        },
        { guests: adults + kids }
    )
    if (days > 2 && days <= 5) {
        stayLength = formatMessage(
            {
                id: 'accommodation.bungalow_offer.2_4_nights',
                defaultMessage: '{nights} noce',
            },
            { nights: days - 1 }
        )
    } else if (days > 5) {
        stayLength = formatMessage(
            {
                id: 'accommodation.bungalow_offer.more_than_4_nights',
                defaultMessage: '{nights} nocy',
            },
            { nights: days - 1 }
        )
    }

    const Gallery = () =>
        pictures ? (
            <RoomTileGallery>
                <GallerySlider
                    images={pictures}
                    alt={rateName}
                    roomName={rateName}
                    testPrefix={`accommo-${bungalowIndex}-room-${roomIndex}`}
                />
            </RoomTileGallery>
        ) : null

    return (
        <RoomTileWrapper
            direction={['column', 'row']}
            align="stretch"
            justify="center"
            pt={[16, 0]}
            pb={[16, 0]}
            pr={[16, 40]}
            pl={[16, 0]}
        >
            {!isTablet && <Gallery />}

            <Box
                ml={[0, 40]}
                mr={[0, 16]}
                pt={[0, 32]}
                pb={[12, 32]}
                mb={[16, 0]}
                gap={[0, 12]}
                direction={['row', 'column']}
                isFullWidth={true}
                justify="space-between"
                wrap="wrap"
            >
                <Box direction="row" isFullWidth gap={12}>
                    <Box isFullWidth={isTablet}>
                        <Text variant="heading3" mb={[4, 12]}>
                            {rateName}
                        </Text>
                        <InlineTextList
                            items={getBedsInfo()}
                            variant="body"
                            color="grayscale_700"
                        />
                    </Box>

                    {isTablet && <Gallery />}
                </Box>

                {!!badges?.length && <RoomTileBadges badgesData={badges} />}
            </Box>
            <Box
                direction={['row-reverse', 'column']}
                justify={['space-between', 'flex-start']}
                align={['center', 'flex-end']}
                isFullWidth={isTablet}
                pt={[0, 32]}
            >
                <Box align="flex-end">
                    {!!totalPrice && <Price value={totalPrice} />}
                    {isTablet && (
                        <Text
                            variant="bodySmall"
                            color="grayscale_700"
                            hasNoWrap={true}
                        >
                            {guests}, {stayLength}
                        </Text>
                    )}
                </Box>
                <CtaWrapper>
                    <Button
                        variant={
                            isThisRoomInRedux ? 'borderSecondary' : 'secondary'
                        }
                        onClick={
                            isThisRoomInRedux
                                ? handleRemoveRoom
                                : handleChooseRoom
                        }
                        isLoading={isAccommodationLoading}
                        hasArrow={false}
                        testId={`accommo-${bungalowIndex}-room-${roomIndex}-${
                            isThisRoomInRedux ? 'remove' : 'add'
                        }`}
                    >
                        {isThisRoomInRedux ? (
                            <FormattedMessage
                                id="global.btn.cancel"
                                defaultMessage="Anuluj"
                            />
                        ) : (
                            <FormattedMessage
                                id="global.btn.select"
                                defaultMessage="Wybierz"
                            />
                        )}
                    </Button>
                </CtaWrapper>
                {!isTablet && (
                    <Text
                        variant="bodySmall"
                        color="grayscale_700"
                        mt={8}
                        hasNoWrap={true}
                    >
                        {guests}, {stayLength}
                    </Text>
                )}
            </Box>
        </RoomTileWrapper>
    )
}

export default RoomTile
